import { useEffect, useRef, useState } from 'react';
import { safetyKitApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import ExcelUploadMenu from '../../components/common/ExcelUploadMenu';
import DeleteBlockedModal from '../../components/common/DeleteBlockedModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MaterialInfoFieldsModal from '../../components/common/MaterialInfoFieldsModal';
import { MATERIAL_TYPES, materialTypeBadgeClass, materialTypeLabel } from '../../constants/materialTypes';

const empty = { name: '', description: '', unit: 'pcs', price: '', validityDays: '', type: 'SAFETY_ITEMS', maxQuantityPerWorker: '1', requiresMaterialId: false };

const TypeBadge = ({ type }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${materialTypeBadgeClass(type)}`}>
    {materialTypeLabel(type)}
  </span>
);

export default function SafetyKitManagement() {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  // Snapshot of requiresMaterialId at the moment Edit was opened — lets the
  // checkbox's onChange detect a real transition and confirm it, without
  // nagging on an unrelated save where the flag never changed.
  const [origRequiresMaterialId, setOrigRequiresMaterialId] = useState(false);
  const [infoModalKit, setInfoModalKit] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [showAllTemplateWarning, setShowAllTemplateWarning] = useState(false);
  const [materialIdToggleConfirm, setMaterialIdToggleConfirm] = useState(null); // { next }

  const load = async () => {
    try {
      const res = await safetyKitApi.list();
      setKits(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDownloadTemplate = async () => {
    setError('');
    if (typeFilter === 'ALL') {
      setShowAllTemplateWarning(true);
      return;
    }
    try {
      const res = await safetyKitApi.downloadTemplate(typeFilter);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `materials_${typeFilter.toLowerCase()}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.response?.data?.message || 'Failed to download template'); }
  };

  const handleBulkUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBulkLoading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await safetyKitApi.bulkUpload(formData);
      setBulkResult(res.data.data);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Bulk upload failed'); }
    finally { setBulkLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...form,
        price:       form.price !== '' ? parseFloat(form.price) : null,
        validityDays: form.validityDays !== '' ? parseInt(form.validityDays) : null,
        maxQuantityPerWorker: form.maxQuantityPerWorker !== '' ? parseInt(form.maxQuantityPerWorker) : 1,
        requiresMaterialId: !!form.requiresMaterialId,
      };
      editId ? await safetyKitApi.update(editId, payload) : await safetyKitApi.create(payload);
      setShowForm(false);
      setForm(empty);
      setEditId(null);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error saving'); }
  };

  const openDelete = async (k) => {
    setError(''); setDeleteError('');
    try {
      const res = await safetyKitApi.checkDelete(k.id);
      setDeleteCheck({ kit: k, ...res.data.data });
    } catch (err) { setError(err.response?.data?.message || 'Error checking material'); }
  };

  const handleDelete = async (deleteOpenRequirements = false) => {
    if (!deleteCheck) return;
    setDeleteError('');
    try {
      await safetyKitApi.remove(deleteCheck.kit.id, deleteOpenRequirements);
      setDeleteCheck(null);
      load();
    } catch (err) { setDeleteError(err.response?.data?.message || 'Error deleting material'); }
  };

  const toggleActive = async (id) => {
    setError('');
    try {
      await safetyKitApi.toggleActive(id);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error toggling material'); }
  };

  const handleDisableInstead = async () => {
    if (!deleteCheck) return;
    setDeleteError('');
    try {
      await safetyKitApi.toggleActive(deleteCheck.kit.id);
      setDeleteCheck(null);
      load();
    } catch (err) { setDeleteError(err.response?.data?.message || 'Error disabling material'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="Materials Master"
        subtitle="Configure material types, pricing and validity periods"
        action={
          <div className="flex gap-2">
            <ExcelUploadMenu
              loading={bulkLoading}
              onDownloadTemplate={handleDownloadTemplate}
              onUploadClick={() => fileInputRef.current?.click()}
            />
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUploadChange} />
            <button className="btn-primary" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}>+ Add Material</button>
          </div>
        }
      />

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>
      )}

      <div className="flex gap-3 mb-4 flex-wrap items-center justify-between">
        <div className="flex gap-1 flex-wrap">
          <button className={typeFilter === 'ALL' ? 'btn-primary' : 'btn-secondary'} onClick={() => setTypeFilter('ALL')}>All</button>
          {MATERIAL_TYPES.map((t) => (
            <button key={t.value} className={typeFilter === t.value ? 'btn-primary' : 'btn-secondary'} onClick={() => setTypeFilter(t.value)}>{t.label}</button>
          ))}
        </div>
        <input
          className="input max-w-xs"
          placeholder="Search materials…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Unit</th>
              <th>Price (₹)</th>
              <th>Validity (Days)</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {kits
              .filter((k) => typeFilter === 'ALL' || k.type === typeFilter)
              .filter((k) => !search || k.name.toLowerCase().includes(search.toLowerCase()) || (k.description || '').toLowerCase().includes(search.toLowerCase()))
              .map((k) => (
              <tr key={k.id}>
                <td className="font-medium">
                  {k.requiresMaterialId ? (
                    <button className="hover:underline text-left" onClick={() => setInfoModalKit(k)}>{k.name}</button>
                  ) : k.name}
                </td>
                <td><TypeBadge type={k.type} /></td>
                <td className="text-gray-500">{k.unit}</td>
                <td>{k.price != null ? `₹${parseFloat(k.price).toFixed(2)}` : <span className="text-gray-300">—</span>}</td>
                <td>{k.validityDays != null ? `${k.validityDays} days` : <span className="text-gray-300">—</span>}</td>
                <td className="text-gray-500 max-w-xs truncate">{k.description || '—'}</td>
                <td><StatusBadge status={k.isActive ? 'active' : 'inactive'} /></td>
                <td>
                  <div className="flex gap-2">
                    <button
                      className="text-blue-600 hover:underline text-sm"
                      onClick={() => {
                        setForm({
                          name:        k.name,
                          description: k.description || '',
                          unit:        k.unit,
                          price:       k.price != null ? String(k.price) : '',
                          validityDays: k.validityDays != null ? String(k.validityDays) : '',
                          isActive:    k.isActive,
                          type:        k.type,
                          maxQuantityPerWorker: String(k.maxQuantityPerWorker ?? 1),
                          requiresMaterialId: !!k.requiresMaterialId,
                        });
                        setEditId(k.id);
                        setOrigRequiresMaterialId(!!k.requiresMaterialId);
                        setShowForm(true);
                      }}
                    >Edit</button>
                    <button
                      className={`text-sm hover:underline ${k.isActive ? 'text-orange-500' : 'text-green-600'}`}
                      onClick={() => toggleActive(k.id)}
                    >
                      {k.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button className="text-red-600 hover:underline text-sm" onClick={() => openDelete(k)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{editId ? 'Edit' : 'Create'} Material</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Material Name</label>
                <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <label className="label">Type</label>
                <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required>
                  {MATERIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Unit</label>
                  <input className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs, pairs..." />
                </div>
                <div>
                  <label className="label">Price (₹)</label>
                  <input className="input" type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Optional" />
                </div>
                <div>
                  <label className="label">Validity (Days)</label>
                  <input className="input" type="number" min="1" value={form.validityDays} onChange={(e) => setForm({ ...form, validityDays: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <div>
                <label className="label">Maximum Quantity Allowed <span className="text-gray-400 font-normal">(per worker, across all sites)</span></label>
                <input className="input" type="number" min="1" value={form.maxQuantityPerWorker} onChange={(e) => setForm({ ...form, maxQuantityPerWorker: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.requiresMaterialId}
                  onChange={(e) => {
                    const next = e.target.checked;
                    if (editId && next !== origRequiresMaterialId) {
                      setMaterialIdToggleConfirm({ next });
                      return;
                    }
                    setForm({ ...form, requiresMaterialId: next });
                  }}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Requires Material ID <span className="text-gray-400">(each unit tracked individually with a unique ID)</span></span>
              </label>
              <div>
                <label className="label">Description</label>
                <textarea className="input" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Material Modal */}
      {deleteCheck && (() => {
        const onlyOpenRequirementsBlock =
          deleteCheck.requirements.open > 0 &&
          deleteCheck.inventory === 0 &&
          deleteCheck.workerKits === 0 &&
          (deleteCheck.transfers || 0) === 0 &&
          deleteCheck.requirements.other === 0;
        const blockers = [
          deleteCheck.inventory > 0 && { label: 'Inventory records', count: deleteCheck.inventory, color: 'blue' },
          deleteCheck.workerKits > 0 && { label: 'Worker kit issuances', count: deleteCheck.workerKits, color: 'purple' },
          deleteCheck.transfers > 0 && { label: 'Transfers', count: deleteCheck.transfers, color: 'indigo' },
          deleteCheck.requirements.open > 0 && { label: 'Open requirements', count: deleteCheck.requirements.open, color: 'amber' },
          deleteCheck.requirements.other > 0 && { label: 'Other requirements (fulfilled / cancelled)', count: deleteCheck.requirements.other, color: 'gray' },
        ].filter(Boolean);
        const canDisableInstead = !onlyOpenRequirementsBlock && !deleteCheck.safe && deleteCheck.kit.isActive;

        let resolutionProps;
        if (onlyOpenRequirementsBlock) {
          resolutionProps = {
            resolutionAvailable: true,
            resolutionLabel: `Delete Open Requirement${deleteCheck.requirements.open > 1 ? 's' : ''} & Retry`,
            resolutionHint: `Deleting the open requirement${deleteCheck.requirements.open > 1 ? 's' : ''} will let you delete this material.`,
            onResolve: () => handleDelete(true),
          };
        } else if (canDisableInstead) {
          resolutionProps = {
            resolutionAvailable: true,
            resolutionLabel: 'Disable Instead',
            resolutionHint: 'This material has usage history and can never be deleted — disable it instead to stop it being used for new inventory, issuances, or requirements.',
            onResolve: handleDisableInstead,
          };
        } else {
          resolutionProps = { resolutionAvailable: false };
        }

        return (
          <DeleteBlockedModal
            title="Delete Material"
            itemName={deleteCheck.kit.name}
            safe={deleteCheck.safe}
            blockers={blockers}
            {...resolutionProps}
            blockedHint="This material is already disabled and has usage history — it can never be deleted."
            onConfirmDelete={() => handleDelete(false)}
            onClose={() => setDeleteCheck(null)}
            error={deleteError}
          />
        );
      })()}

      {/* Excel Upload Results Modal */}
      {bulkResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Excel Upload Results</h3>
              <button onClick={() => setBulkResult(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>
            <div className="space-y-3">
              <div className="flex gap-4 text-sm flex-wrap">
                <span className="text-green-700 font-medium">{bulkResult.created} created</span>
                <span className="text-blue-700 font-medium">{bulkResult.updated || 0} updated</span>
                <span className="text-red-600 font-medium">{bulkResult.failed} failed</span>
                <span className="text-gray-400">{bulkResult.total} total row{bulkResult.total !== 1 ? 's' : ''}</span>
              </div>
              {bulkResult.failedRecords?.length > 0 && (
                <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Identifier</th>
                        <th className="text-left px-3 py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResult.failedRecords.map((f, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-1.5">{f.row}</td>
                          <td className="px-3 py-1.5">{f.identifier}</td>
                          <td className="px-3 py-1.5 text-red-600">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
              <button className="btn-primary" onClick={() => setBulkResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* "All" selected — template can't be downloaded */}
      {showAllTemplateWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-50">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-500">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-slate-900">Can't Download Template for "All"</h3>
              </div>
              <button onClick={() => setShowAllTemplateWarning(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Each material type has its own bulk-upload template (different columns and validation rules), so there's no single template that covers every type at once. Select a specific material type from the filters above, then download its template.
            </p>
            <div className="flex justify-end pt-3 border-t border-gray-100">
              <button className="btn-primary" onClick={() => setShowAllTemplateWarning(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!materialIdToggleConfirm}
        title="Change Material ID Requirement?"
        message={materialIdToggleConfirm?.next
          ? 'Turning this ON only affects future transactions — existing stock keeps its current state. Issuing or returning this material will require a Material ID from now on.'
          : 'Turning this OFF only affects future transactions — existing Material IDs are kept for history but hidden from new issue/return/transfer pickers.'}
        danger={false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={() => { setForm({ ...form, requiresMaterialId: materialIdToggleConfirm.next }); setMaterialIdToggleConfirm(null); }}
        onCancel={() => setMaterialIdToggleConfirm(null)}
      />

      <MaterialInfoFieldsModal
        show={!!infoModalKit}
        onClose={() => setInfoModalKit(null)}
        kitId={infoModalKit?.id}
        materialName={infoModalKit?.name}
      />
    </div>
  );
}
